import React from "react";
import { Container } from "reactstrap";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

import {
  DogPage,
  DogFactsBar,
  DogHealthGenetics,
  RichProfileText
} from "components/Dogs/DogComponents.js";
import SEO from "components/SEO/SEO.js";

const stackImage = require("../../assets/img/dogs/profile-squeeze.jpg");
const pedigreeImage = require("../../assets/img/dogs/litters/vr2023/pedigree.jpg");
const venusProfileImage = require("../../assets/img/dogs/profile-venus.jpg");

const pedigreeData = {
  image: pedigreeImage,
  alt: "Venus x Rip pedigree",
  linkedDogs: [
    {
      id: "venus",
      name: "Venus",
      relationship: "Dam",
      breed: "Border Collie",
      image: venusProfileImage,
      profileHref: "/dogs/venus"
    }
  ]
};

const seekerProfile = {
  name: "Seeker",
  registeredName: "Endgame's Source of Magic",
  about: "Seeker is a male Border Collie from the Venus × Rip 2023 litter. He is currently training competitively in agility and is known for his athleticism, confidence, and willingness to work.",
  facts: [
    { label: "Breed", value: "Border Collie" },
    { label: "Born", value: "05/13/23" },
    { label: "Height", value: '21"' },
    { label: "Weight", value: "45lbs" },
    { label: "Owner", value: "Karah Gerelus" }
  ],
  temperament: "Seeker is high drive during work with medium arousal. He’s pushy, hard headed, and confident, yet biddable, calm and smooth. Both food and toys are rewarding for him, however, he definitely favors tug more during agility. At home, he’s easy to live with, low arousal in the house, and loves relaxing and cuddling. He’s a “do everything” kind of dog and shows no sensitivities in busy, loud environments. Seeker is dog neutral, puppy friendly, people neutral to friendly.",
  athleticAbility: "Seeker is already showing the style and drive that make a strong agility prospect. He is athletic, confident, and eager to work through challenges, with a smooth, forward-moving style that suits the sport well.",
};

const healthGroups = [
  {
    title: "Orthopedic & Screening",
    items: [
      { label: "Hips", value: "Pennhip .37/.34, OFA Excellent" },
      { label: "Elbows", value: "Normal" },
      { label: "Shoulders", value: "Normal" },
      { label: "Eyes", value: "Normal" },
      { label: "Hearing", value: "BAER Normal" }
    ]
  },
  {
    title: "DNA Panel",
    items: [
      { label: "CEA", value: "Carrier" },
      { label: "DM", value: "Clear" },
      { label: "SN", value: "Clear" },
      { label: "TNS", value: "Clear" },
      { label: "IGS", value: "Clear" },
      { label: "NCL5", value: "Clear" },
      { label: "G/G", value: "Clear" },
      { label: "DH", value: "Clear" },
      { label: "EOD", value: "Clear" }
    ]
  }
];

const clearances = [
  {
    id: "seeker-caer-2023",
    label: "CAER Report 2023",
    shortLabel: "CAER",
    category: "Eyes",
    detailLabel: "Eyes",
    date: "July 2023",
    image: require("../../assets/img/dogs/seeker/clearance6.png"),
    alt: "Seeker CAER report from July 2023"
  },
  {
    id: "seeker-baer-2023",
    label: "BAER Report",
    shortLabel: "BAER",
    category: "Other",
    detailLabel: "Hearing",
    date: "July 2023",
    image: require("../../assets/img/dogs/seeker/clearance5.png"),
    alt: "Seeker BAER report from July 2023"
  },
  {
    id: "seeker-embark-2023",
    label: "Embark Report",
    shortLabel: "Embark",
    category: "Genetic",
    detailLabel: "DNA",
    date: "November 2023",
    image: require("../../assets/img/dogs/seeker/clearance4.png"),
    alt: "Seeker Embark report from March 2021"
  },
  {
    id: "seeker-ofa-elbows-2026",
    label: "OFA Elbow Evaluation",
    shortLabel: "Elbows",
    category: "Orthopedic",
    detailLabel: "Elbows",
    date: "October 2026",
    image: require("../../assets/img/dogs/seeker/clearance1.png"),
    alt: "Seeker OFA elbow evaluation showing normal elbows from October 2026"
  },
  {
    id: "seeker-ofa-hips-2026",
    label: "OFA Hip Evaluation",
    shortLabel: "Hips",
    category: "Orthopedic",
    detailLabel: "Hips",
    date: "October 2026",
    image: require("../../assets/img/dogs/seeker/clearance2.png"),
    alt: "Seeker OFA hip evaluation rated Excellent from October 2026"
  },
  {
    id: "seeker-pennhip-2024",
    label: "Pennhip Evaluation",
    shortLabel: "Hips",
    category: "Orthopedic",
    detailLabel: "Hips",
    date: "October 2024",
    image: require("../../assets/img/dogs/seeker/clearance7.png"),
    alt: "Seeker Pennhip evaluation .37/.34"
  },
  {
    id: "seeker-ofa-shoulders-2026",
    label: "OFA Shoulder Evaluation",
    shortLabel: "Shoulders",
    category: "Orthopedic",
    detailLabel: "Shoulders",
    date: "October 2026",
    image: require("../../assets/img/dogs/seeker/clearance3.png"),
    alt: "Seeker OFA shoulder evaluation showing normal shoulders from October 2026"
  },
];


function DogSectionHeading({ eyebrow, title, children }) {
  return (
    <div className="dog-detail-section-heading">
      {eyebrow && <p>{eyebrow}</p>}
      <h2>{title}</h2>
      <span aria-hidden="true"></span>
      {children}
    </div>
  );
}

function SeekerPage() {
  return (
    <>
      <SEO
        title="Seeker | Border Collie Sport Dog"
        description="Seeker is a blue merle border collie from the Venus x Rip 2023 litter. He lives and trains with his owner Karah Gerelus in Alberta, Canada. He is available for stud for health tested females on a limited basis. Contact his owner for more information."
        path="/dogs/seeker"
      />
      <DogPage
        headerBg="squeeze"
        pageClassName="dog-detail-page dog-seeker-page"
      >
        <section className="dog-facts-wrap" aria-label="Seeker at a glance">
          <Container>
            <DogFactsBar facts={seekerProfile.facts} />
          </Container>
        </section>

        <section className="dog-detail-section dog-about-section">
          <Container>
            <div className="dog-about-layout">
              <div className="dog-about-copy">
                <div className="dog-back-link">
                  <Link to="/dogs">
                    <ArrowLeft aria-hidden="true" size={14} strokeWidth={2.2} /> Back to Dogs
                  </Link>
                </div>
                <DogSectionHeading eyebrow={seekerProfile.registeredName} title={`About ${seekerProfile.name}`} />
                <p><RichProfileText>{seekerProfile.about}</RichProfileText></p>
              </div>
              <figure className="dog-stack-figure">
                <img alt="Seeker profile placeholder" src={stackImage} />
                <figcaption>{seekerProfile.name}</figcaption>
              </figure>
            </div>

            <DogHealthGenetics
              dogName={seekerProfile.name}
              breed="Border Collie"
              healthGroups={healthGroups}
              pedigreeData={pedigreeData}
              clearances={clearances}
            />

            <div className="dog-traits-layout">
              <article>
                <h2>Temperament</h2>
                <span aria-hidden="true"></span>
                <p><RichProfileText>{seekerProfile.temperament}</RichProfileText></p>
              </article>
              <article>
                <h2>Athletic Ability</h2>
                <span aria-hidden="true"></span>
                <p><RichProfileText>{seekerProfile.athleticAbility}</RichProfileText></p>
              </article>
            </div>
          </Container>
        </section>
      </DogPage>
    </>
  );
}

export default SeekerPage;
